import React, { useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatMXN, fileToBase64 } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "../components/ui/drawer";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Users, CreditCard, MapPin, Store, CheckCircle2, XCircle, Clock, Eye, Search, Tag, Edit3, HandCoins, Trophy, Megaphone, Sparkles, KeyRound, Gift, RotateCcw, ImagePlus, LogIn, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PromotersAdmin from "../components/PromotersAdmin";
import RaffleAdmin from "../components/RaffleAdmin";
import EditionAdmin from "../components/EditionAdmin";
import AdminAdAssistant from "../components/AdminAdAssistant";
import AccessPinsAdmin from "../components/AccessPinsAdmin";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="overline text-[hsl(var(--primary))]">Super Admin</div>
        <h1 className="font-display text-4xl tracking-tight mt-2">Panel de control</h1>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="admin-stats">
          <Stat icon={<Store className="w-5 h-5" />} label="Comercios" value={stats?.total_businesses ?? "—"} />
          <Stat icon={<CheckCircle2 className="w-5 h-5" />} label="Suscripciones activas" value={stats?.active_subscriptions ?? "—"} />
          <Stat icon={<MapPin className="w-5 h-5" />} label="Directorios (CP)" value={stats?.directories_count ?? "—"} />
          <Stat icon={<CreditCard className="w-5 h-5" />} label="Ingreso aprobado" value={stats ? formatMXN(stats.revenue_total) : "—"} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          <Stat icon={<Clock className="w-5 h-5" />} label="Pagos por revisar" value={stats?.pending_payments ?? "—"} highlight />
          <Stat icon={<Users className="w-5 h-5" />} label="Usuarios activos" value={`${stats?.active_users ?? "—"} / ${stats?.total_users ?? "—"}`} />
          <Stat icon={<Wand label="ads" />} label="Anuncios creados" value={stats?.total_ads ?? "—"} />
        </div>

        <Tabs defaultValue="payments" className="mt-10">
          <TabsList className="bg-white border border-border flex flex-wrap h-auto" data-testid="admin-tabs">
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Pagos</TabsTrigger>
            <TabsTrigger value="businesses" data-testid="admin-tab-businesses">Comercios</TabsTrigger>
            <TabsTrigger value="ad-assist" data-testid="admin-tab-ad-assist">
              <Sparkles className="w-3.5 h-3.5 mr-1 text-[#7C3AED]" />Asistente IA
            </TabsTrigger>
            <TabsTrigger value="directories" data-testid="admin-tab-directories"><Tag className="w-3.5 h-3.5 mr-1" />Directorios</TabsTrigger>
            <TabsTrigger value="edition" data-testid="admin-tab-edition"><Megaphone className="w-3.5 h-3.5 mr-1" />Edición</TabsTrigger>
            <TabsTrigger value="promoters" data-testid="admin-tab-promoters"><HandCoins className="w-3.5 h-3.5 mr-1" />Promotores</TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers"><Gift className="w-3.5 h-3.5 mr-1" />Clientes</TabsTrigger>
            <TabsTrigger value="raffle" data-testid="admin-tab-raffle"><Trophy className="w-3.5 h-3.5 mr-1" />Rifa mensual</TabsTrigger>
            <TabsTrigger value="pins" data-testid="admin-tab-pins"><KeyRound className="w-3.5 h-3.5 mr-1" />PINs acceso</TabsTrigger>
          </TabsList>
          <TabsContent value="payments" className="mt-6"><PaymentsTab onChange={() => api.get("/admin/stats").then((r) => setStats(r.data))} /></TabsContent>
          <TabsContent value="businesses" className="mt-6"><BusinessesTab onChange={() => api.get("/admin/stats").then((r) => setStats(r.data))} /></TabsContent>
          <TabsContent value="ad-assist" className="mt-6"><AdminAdAssistant /></TabsContent>
          <TabsContent value="directories" className="mt-6"><DirectoriesTab /></TabsContent>
          <TabsContent value="edition" className="mt-6"><EditionAdmin /></TabsContent>
          <TabsContent value="promoters" className="mt-6"><PromotersAdmin /></TabsContent>
          <TabsContent value="customers" className="mt-6"><CustomersTab /></TabsContent>
          <TabsContent value="raffle" className="mt-6"><RaffleAdmin /></TabsContent>
          <TabsContent value="pins" className="mt-6"><AccessPinsAdmin /></TabsContent>
        </Tabs>
      </section>
      <Footer />
    </div>
  );
}

const Wand = () => <CreditCard className="w-5 h-5" />;

const Stat = ({ icon, label, value, highlight }) => (
  <div className={`rounded-2xl border p-5 ${highlight ? "bg-[#F5F3FF] border-[#7C3AED]/30" : "bg-white border-border"}`}>
    <div className="flex items-center gap-2 text-muted-foreground">
      {icon}
      <span className="overline text-xs">{label}</span>
    </div>
    <div className="font-display text-3xl mt-2">{value}</div>
  </div>
);

const PAYMENT_FILTERS = [
  { key: "needs_review", label: "Por revisar", query: "?needs_review=true" },
  { key: "verified", label: "Verificados", query: "?status=approved&needs_review=false" },
  { key: "cancelled", label: "Cancelados", query: "?status=cancelled" },
];

function PaymentsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("needs_review");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [notes, setNotes] = useState("");
  const [distBusy, setDistBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const q = PAYMENT_FILTERS.find((f) => f.key === filter)?.query || "?needs_review=true";
    const { data } = await api.get(`/admin/payments${q}`);
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const openView = async (p) => {
    const { data } = await api.get(`/admin/payments/${p.id}`);
    setViewing({ ...p, ...data });
    setNotes("");
  };

  const cancel = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/cancel`, { notes });
      toast.success("Pago cancelado y suscripción revertida");
      setViewing(null); load(); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const reverify = async (id) => {
    try {
      await api.post(`/admin/payments/${id}/reverify`);
      toast.success("Pago marcado como verificado");
      setViewing(null); load(); onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const distributeCommission = async (id) => {
    setDistBusy(true);
    try {
      const { data } = await api.post(`/admin/payments/${id}/distribute-commission`);
      if (data.created) {
        toast.success("Comisión de $100 MXN generada para el promotor");
      } else {
        toast.info("Comisión ya existía — sin cambios");
      }
      const { data: updated } = await api.get(`/admin/payments/${id}`);
      setViewing((v) => ({ ...v, ...updated }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setDistBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h3 className="font-display text-2xl tracking-tight">Pagos</h3>
        <div className="flex gap-2 flex-wrap">
          {PAYMENT_FILTERS.map(({ key, label }) => (
            <Button key={key} variant={filter === key ? "default" : "outline"} size="sm" onClick={() => setFilter(key)} className={filter === key ? "btn-primary" : ""} data-testid={`pay-filter-${key}`}>
              {label}
            </Button>
          ))}
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Cargando…</div> : items.length === 0 ? (
        <div className="text-muted-foreground text-sm">Sin pagos en este estado.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">Comercio</th><th>CP</th><th>WhatsApp</th><th>Monto</th><th>Referencia</th><th>Fecha</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-border" data-testid={`admin-pay-row-${p.id}`}>
                  <td className="py-3">{p.business_name}</td>
                  <td>{p.postal_code}</td>
                  <td className="font-mono text-xs">{p.whatsapp}</td>
                  <td>{formatMXN(p.amount)}</td>
                  <td className="text-xs">{p.reference || "—"}</td>
                  <td className="text-xs">{new Date(p.created_at).toLocaleString("es-MX")}</td>
                  <td><Button size="sm" variant="outline" onClick={() => openView(p)} data-testid={`pay-view-${p.id}`}><Eye className="w-4 h-4 mr-1" />Ver</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-pay-dialog">
          <DialogHeader><DialogTitle>{viewing?.business_name} · {formatMXN(viewing?.amount || 0)}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div><strong>CP:</strong> {viewing.postal_code} · <strong>WhatsApp:</strong> {viewing.whatsapp}</div>
                <div><strong>Referencia:</strong> {viewing.reference || "—"}</div>
                <div><strong>Enviado:</strong> {new Date(viewing.created_at).toLocaleString("es-MX")}</div>
                {viewing.verified_at && <div><strong>Verificado:</strong> {new Date(viewing.verified_at).toLocaleString("es-MX")}</div>}
                <div className="flex items-center gap-2"><strong>Estado:</strong> <StatusBadge status={viewing.status} needsReview={viewing.needs_review} /></div>
                {viewing.promoter_name && (
                  <div className="flex items-center justify-between pt-2 border-t border-border mt-2">
                    <div><strong>Promotor:</strong> {viewing.promoter_name} · {viewing.commission ? <span className="text-green-700 font-medium">Comisión distribuida ✓</span> : <span className="text-amber-600 font-medium">Sin comisión</span>}</div>
                    {!viewing.commission && viewing.status === "approved" && (
                      <Button size="sm" variant="outline" onClick={() => distributeCommission(viewing.id)} disabled={distBusy} data-testid="pay-distribute-commission">
                        <HandCoins className="w-4 h-4 mr-1" />{distBusy ? "…" : "Distribuir $100"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {viewing.receipt_base64 ? (
                viewing.receipt_base64.startsWith("data:application/pdf") ? (
                  <a className="inline-block text-[hsl(var(--primary))] underline" href={viewing.receipt_base64} target="_blank" rel="noreferrer">Abrir PDF comprobante</a>
                ) : (
                  <img src={viewing.receipt_base64} alt="comprobante" className="max-h-[400px] rounded-lg border border-border mx-auto" />
                )
              ) : null}
              {viewing.needs_review && (
                <>
                  <Input placeholder="Notas (opcional, para cancelación)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-pay-notes" />
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => cancel(viewing.id)} data-testid="admin-pay-cancel"><XCircle className="w-4 h-4 mr-1" />Cancelar pago</Button>
                    <Button className="btn-primary" onClick={() => reverify(viewing.id)} data-testid="admin-pay-reverify"><CheckCircle2 className="w-4 h-4 mr-1" />Marcar verificado</Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const emptyCreateForm = {
  business_name: "", giro: "", whatsapp: "", password: "", address: "",
  postal_code: "", colonia: "", city: "", state: "", email: "",
  promoter_id: "", activate_subscription: false, subscription_months: 1,
};

function BusinessesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [adDialog, setAdDialog] = useState(null);
  const [adLoading, setAdLoading] = useState(false);
  const [adDeleting, setAdDeleting] = useState(false);
  const [adUpload, setAdUpload] = useState(null);
  const [adUploading, setAdUploading] = useState(false);
  const adFileRef = useRef(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const [promotersList, setPromotersList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createBusy, setCreateBusy] = useState(false);
  const { impersonate } = useAuth();

  useEffect(() => {
    api.get("/admin/promoters").then((r) => setPromotersList(r.data)).catch(() => {});
  }, []);

  const enterAs = async (type, id) => {
    try {
      await impersonate(type, id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo entrar a la cuenta");
    }
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/admin/businesses${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (b) => {
    try {
      await api.post(`/admin/businesses/${b.id}/toggle`);
      toast.success("Estado actualizado");
      load(); onChange && onChange();
    } catch (e) { toast.error("Error"); }
  };

  const toggleSubscription = async (b) => {
    try {
      await api.post(`/admin/businesses/${b.id}/toggle-subscription`);
      toast.success(`Suscripción ${b.subscription_active ? "desactivada" : "activada"}`);
      load();
    } catch (e) { toast.error("Error"); }
  };

  const resetAiCounter = async (b) => {
    try {
      await api.post(`/admin/businesses/${b.id}/reset-ai-counter`);
      toast.success(`Contador IA reiniciado para ${b.business_name}`);
      load();
    } catch (e) { toast.error("Error al reiniciar contador"); }
  };

  const viewAd = async (b) => {
    setAdLoading(true);
    setAdUpload(null);
    setAdDialog({ business: b, ad: null });
    try {
      const { data } = await api.get(`/admin/businesses/${b.id}/ad`);
      setAdDialog({ business: b, ad: data.ad });
    } catch { toast.error("Error al cargar anuncio"); setAdDialog(null); }
    setAdLoading(false);
  };

  const deleteAd = async () => {
    if (!adDialog) return;
    setAdDeleting(true);
    try {
      await api.delete(`/admin/businesses/${adDialog.business.id}/ad`);
      toast.success("Anuncio eliminado");
      setAdDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    setAdDeleting(false);
  };

  const resetAdFileInput = () => { if (adFileRef.current) adFileRef.current.value = ""; };

  const handleAdFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5_000_000) {
      toast.error("Imagen muy grande. Máximo 5 MB.");
      resetAdFileInput();
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          if (img.width !== img.height) {
            reject(new Error(`La imagen debe ser cuadrada (1:1). La tuya mide ${img.width}×${img.height} px.`));
          } else if (img.width < 800) {
            reject(new Error(`Resolución mínima 800×800 px. La tuya mide ${img.width}×${img.height} px.`));
          } else {
            resolve();
          }
        };
        img.onerror = () => reject(new Error("No se pudo leer la imagen."));
        img.src = b64;
      });
      setAdUpload(b64);
    } catch (err) {
      toast.error(err.message || "No se pudo leer el archivo");
      resetAdFileInput();
    }
  };

  const uploadAdImage = async () => {
    if (!adUpload || !adDialog) return;
    setAdUploading(true);
    try {
      const { data } = await api.post(`/admin/businesses/${adDialog.business.id}/ads/upload-image`, { image_base64: adUpload });
      toast.success("Imagen publicada");
      setAdDialog({ ...adDialog, ad: data });
      setAdUpload(null);
      resetAdFileInput();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir la imagen"); }
    setAdUploading(false);
  };

  const openEdit = (b) => {
    setEditing(b);
    setEditForm({
      business_name: b.business_name || "",
      giro: b.giro || "",
      address: b.address || "",
      postal_code: b.postal_code || "",
      colonia: b.colonia || "",
      city: b.city || "",
      state: b.state || "",
      whatsapp: b.whatsapp || "",
      email: b.user_email || "",
      password: "",
      promoter_id: b.promoter_id || "",
      horario: b.horario || "",
      loyalty_enabled: b.loyalty_enabled ?? false,
      loyalty_points_per_purchase: b.loyalty_points_per_purchase ?? 10,
      loyalty_purchase_label: b.loyalty_purchase_label || "por cada compra",
      loyalty_threshold: b.loyalty_threshold ?? 100,
      loyalty_reward: b.loyalty_reward || "",
    });
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {};
      const orig = editing;
      for (const [k, v] of Object.entries(editForm)) {
        if (k === "password" && v) { payload.password = v; continue; }
        if (k === "email") {
          const origVal = orig.user_email || "";
          if (v !== origVal) payload.email = v;
          continue;
        }
        if (k === "promoter_id") {
          const origVal = orig.promoter_id || "";
          if (v !== origVal) payload.promoter_id = v || null;
          continue;
        }
        if (["loyalty_enabled", "loyalty_points_per_purchase", "loyalty_threshold"].includes(k)) {
          if (v !== (orig[k] ?? (k === "loyalty_enabled" ? false : 0))) payload[k] = v;
          continue;
        }
        const origKey = k === "whatsapp" ? "whatsapp" : k;
        if (v !== (orig[origKey] || "")) payload[k] = v;
      }
      if (Object.keys(payload).length === 0) {
        toast.error("Sin cambios");
        setEditBusy(false);
        return;
      }
      await api.put(`/admin/businesses/${editing.id}`, payload);
      toast.success("Comercio actualizado");
      setEditing(null);
      load();
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setEditBusy(false); }
  };

  const createBusiness = async () => {
    for (const f of ["business_name", "giro", "whatsapp", "password", "address", "postal_code"]) {
      if (!String(createForm[f] || "").trim()) { toast.error("Completa los campos obligatorios (*)"); return; }
    }
    setCreateBusy(true);
    try {
      await api.post("/admin/businesses", {
        ...createForm,
        subscription_months: Number(createForm.subscription_months) || 1,
      });
      toast.success("Comercio creado");
      setCreating(false);
      setCreateForm(emptyCreateForm);
      load();
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Error creando comercio"); }
    finally { setCreateBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h3 className="font-display text-2xl tracking-tight">Comercios</h3>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o giro" data-testid="admin-biz-search" />
          </div>
          <Button onClick={load} size="sm" data-testid="admin-biz-search-btn">Buscar</Button>
          <Button onClick={() => setCreating(true)} size="sm" className="btn-primary" data-testid="admin-biz-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuevo comercio
          </Button>
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Cargando…</div> : items.length === 0 ? (
        <div className="text-muted-foreground text-sm">Sin comercios.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">Comercio</th><th>Giro</th><th>CP</th><th>Suscripción</th><th>Usuario</th><th>IA / mes</th><th>Anuncio</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-b border-border" data-testid={`admin-biz-row-${b.id}`}>
                  <td className="py-3">
                    <div className="font-medium">{b.business_name}</div>
                    <div className="text-xs text-muted-foreground">{b.user_email}</div>
                  </td>
                  <td>{b.giro}</td>
                  <td>{b.postal_code}</td>
                  <td>
                    {b.subscription_active ? (
                      <Badge className="bg-[#7C3AED] text-white">Activa</Badge>
                    ) : (
                      <Badge variant="outline">Inactiva</Badge>
                    )}
                    {b.subscription_expires_at && (
                      <div className="text-[10px] text-muted-foreground mt-1">vence {new Date(b.subscription_expires_at).toLocaleDateString("es-MX")}</div>
                    )}
                  </td>
                  <td>
                    {b.user_is_active ? <Badge className="bg-[#7C3AED] text-white">Alta</Badge> : <Badge variant="destructive">Baja</Badge>}
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-mono font-semibold ${b.ads_generated_this_month >= 4 ? "text-rose-600" : "text-zinc-600"}`}>
                        {b.ads_generated_this_month ?? 0}/4
                      </span>
                      {(b.ads_generated_this_month ?? 0) > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-muted-foreground hover:text-[#7C3AED]"
                          onClick={() => resetAiCounter(b)}
                          data-testid={`admin-biz-reset-ai-${b.id}`}
                          title="Reiniciar contador IA"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => viewAd(b)} data-testid={`admin-biz-view-ad-${b.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => enterAs("business", b.id)} title="Entrar como este comercio" data-testid={`admin-biz-impersonate-${b.id}`}>
                        <LogIn className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`admin-biz-edit-${b.id}`}>
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggle(b)} data-testid={`admin-biz-toggle-${b.id}`}>
                        {b.user_is_active ? "Baja" : "Alta"}
                      </Button>
                      <Button
                        variant={b.subscription_active ? "outline" : "default"}
                        size="sm"
                        onClick={() => toggleSubscription(b)}
                        data-testid={`admin-biz-toggle-sub-${b.id}`}
                        className={b.subscription_active ? "text-rose-600 border-rose-200 hover:bg-rose-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                      >
                        {b.subscription_active ? "Desactivar" : "Activar"} sub
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!adDialog} onOpenChange={(o) => { if (!o) { setAdDialog(null); setAdUpload(null); resetAdFileInput(); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{adDialog?.business?.business_name} · Anuncio</DialogTitle>
          </DialogHeader>
          {adLoading && <div className="text-muted-foreground text-sm py-4">Cargando…</div>}
          {!adLoading && adDialog && !adDialog.ad && !adUpload && (
            <div className="text-muted-foreground text-sm py-4">Sin anuncio publicado.</div>
          )}
          {!adLoading && adDialog?.ad && (
            <div className="space-y-4">
              {adDialog.ad.image_base64 && (
                <img
                  src={adDialog.ad.image_base64.startsWith("data:") ? adDialog.ad.image_base64 : `data:image/png;base64,${adDialog.ad.image_base64}`}
                  alt="Anuncio"
                  className="w-full rounded-xl border border-border object-contain"
                />
              )}
              <div className="text-sm space-y-1">
                <div><strong>Título:</strong> {adDialog.ad.title}</div>
                {adDialog.ad.description && <div><strong>Descripción:</strong> {adDialog.ad.description}</div>}
                {adDialog.ad.discount_text && <div><strong>Promoción:</strong> {adDialog.ad.discount_text}</div>}
                <div><strong>Aprobado:</strong> {adDialog.ad.is_approved ? "Sí" : "No"}</div>
              </div>
            </div>
          )}
          {!adLoading && adDialog && (
            <div className="space-y-3 pt-1">
              {adUpload ? (
                <div className="space-y-3" data-testid="admin-biz-upload-preview">
                  <div className="overline text-muted-foreground">Nueva imagen</div>
                  <img src={adUpload} alt="Nueva imagen" className="w-full max-h-[300px] rounded-xl border border-border object-contain" />
                  <div className="text-[11px] text-muted-foreground">
                    Se {adDialog.ad ? "reemplazará la imagen actual" : "creará el anuncio con esta imagen"} y quedará publicada.
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => { setAdUpload(null); resetAdFileInput(); }} data-testid="admin-biz-upload-cancel">
                      Cancelar
                    </Button>
                    <Button className="btn-primary" size="sm" onClick={uploadAdImage} disabled={adUploading} data-testid="admin-biz-upload-confirm">
                      <ImagePlus className="w-4 h-4 mr-1" />{adUploading ? "Subiendo…" : "Publicar imagen"}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => adFileRef.current?.click()} data-testid="admin-biz-upload-ad">
                  <ImagePlus className="w-4 h-4 mr-1" />{adDialog.ad ? "Cambiar imagen" : "Subir imagen de anuncio"}
                </Button>
              )}
              <input
                ref={adFileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAdFile}
                data-testid="admin-biz-upload-input"
              />
              {adDialog.ad && (
                <div className="pt-1">
                  <Button variant="destructive" size="sm" onClick={deleteAd} disabled={adDeleting} data-testid="admin-biz-delete-ad">
                    <XCircle className="w-4 h-4 mr-1" />{adDeleting ? "Eliminando…" : "Eliminar anuncio"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create business dialog */}
      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-biz-create-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo comercio</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Nombre del comercio *</Label>
              <Input value={createForm.business_name} onChange={(e) => setCreateForm({ ...createForm, business_name: e.target.value })} data-testid="biz-create-name" />
            </div>
            <div>
              <Label className="text-sm">Giro *</Label>
              <Input value={createForm.giro} onChange={(e) => setCreateForm({ ...createForm, giro: e.target.value })} placeholder="Ej. Tacos, Farmacia" data-testid="biz-create-giro" />
            </div>
            <div>
              <Label className="text-sm">WhatsApp *</Label>
              <Input value={createForm.whatsapp} onChange={(e) => setCreateForm({ ...createForm, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 12) })} inputMode="numeric" placeholder="5512345678" data-testid="biz-create-whatsapp" />
            </div>
            <div>
              <Label className="text-sm">Contraseña *</Label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Mínimo 6 caracteres" data-testid="biz-create-password" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Dirección *</Label>
              <Input value={createForm.address} onChange={(e) => setCreateForm({ ...createForm, address: e.target.value })} data-testid="biz-create-address" />
            </div>
            <div>
              <Label className="text-sm">Código Postal *</Label>
              <Input value={createForm.postal_code} onChange={(e) => setCreateForm({ ...createForm, postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} inputMode="numeric" data-testid="biz-create-cp" />
            </div>
            <div>
              <Label className="text-sm">Colonia</Label>
              <Input value={createForm.colonia} onChange={(e) => setCreateForm({ ...createForm, colonia: e.target.value })} data-testid="biz-create-colonia" />
            </div>
            <div>
              <Label className="text-sm">Ciudad</Label>
              <Input value={createForm.city} onChange={(e) => setCreateForm({ ...createForm, city: e.target.value })} data-testid="biz-create-city" />
            </div>
            <div>
              <Label className="text-sm">Estado</Label>
              <Input value={createForm.state} onChange={(e) => setCreateForm({ ...createForm, state: e.target.value })} data-testid="biz-create-state" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Correo electrónico (opcional)</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="biz-create-email" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm">Promotor</Label>
              <Select value={createForm.promoter_id || "__none__"} onValueChange={(v) => setCreateForm({ ...createForm, promoter_id: v === "__none__" ? "" : v })} data-testid="biz-create-promoter">
                <SelectTrigger>
                  <SelectValue placeholder="Sin promotor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin promotor</SelectItem>
                  {promotersList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Activar suscripción de inmediato</Label>
                <Switch checked={createForm.activate_subscription} onCheckedChange={(v) => setCreateForm({ ...createForm, activate_subscription: v })} data-testid="biz-create-sub-toggle" />
              </div>
              {createForm.activate_subscription && (
                <div className="mt-3 flex items-center gap-3">
                  <Label className="text-sm shrink-0">Meses de cortesía</Label>
                  <Input type="number" min={1} max={24} value={createForm.subscription_months} onChange={(e) => setCreateForm({ ...createForm, subscription_months: e.target.value })} className="w-24" data-testid="biz-create-sub-months" />
                  <span className="text-xs text-muted-foreground">30 días por mes</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button className="btn-primary" onClick={createBusiness} disabled={createBusy} data-testid="biz-create-submit">
              {createBusy ? "Creando…" : "Crear comercio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Drawer open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DrawerContent className="max-h-[90vh]" data-testid="admin-biz-edit-drawer">
          <DrawerHeader>
            <DrawerTitle>Editar comercio</DrawerTitle>
          </DrawerHeader>
          {editing && (
            <div className="px-4 pb-6 space-y-4 overflow-y-auto">
              <div>
                <Label className="text-sm">Nombre del comercio</Label>
                <Input value={editForm.business_name} onChange={(e) => setEditForm({ ...editForm, business_name: e.target.value })} data-testid="biz-edit-name" />
              </div>
              <div>
                <Label className="text-sm">Giro</Label>
                <Input value={editForm.giro} onChange={(e) => setEditForm({ ...editForm, giro: e.target.value })} data-testid="biz-edit-giro" />
              </div>
              <div>
                <Label className="text-sm">Dirección</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="biz-edit-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Código Postal</Label>
                  <Input value={editForm.postal_code} onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })} data-testid="biz-edit-cp" />
                </div>
                <div>
                  <Label className="text-sm">WhatsApp</Label>
                  <Input value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 12) })} inputMode="numeric" data-testid="biz-edit-whatsapp" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-sm">Colonia</Label>
                  <Input value={editForm.colonia} onChange={(e) => setEditForm({ ...editForm, colonia: e.target.value })} data-testid="biz-edit-colonia" />
                </div>
                <div>
                  <Label className="text-sm">Ciudad</Label>
                  <Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} data-testid="biz-edit-city" />
                </div>
                <div>
                  <Label className="text-sm">Estado</Label>
                  <Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} data-testid="biz-edit-state" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Correo electrónico</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="biz-edit-email" />
              </div>
              <div>
                <Label className="text-sm">Nueva contraseña <span className="text-muted-foreground font-normal">(dejar vacío para no cambiar)</span></Label>
                <Input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nueva contraseña" data-testid="biz-edit-password" />
              </div>
              <div>
                <Label className="text-sm">Promotor</Label>
                <Select value={editForm.promoter_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, promoter_id: v === "__none__" ? "" : v })} data-testid="biz-edit-promoter">
                  <SelectTrigger>
                    <SelectValue placeholder="Sin promotor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin promotor</SelectItem>
                    {promotersList.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Horario</Label>
                <Input value={editForm.horario || ""} onChange={(e) => setEditForm({ ...editForm, horario: e.target.value })} placeholder="Ej: Lun-Vie 9:00-18:00" data-testid="biz-edit-horario" />
              </div>
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Programa de lealtad</Label>
                  <Switch checked={!!editForm.loyalty_enabled} onCheckedChange={(v) => setEditForm({ ...editForm, loyalty_enabled: v })} data-testid="biz-edit-loyalty-toggle" />
                </div>
                {editForm.loyalty_enabled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Puntos por compra</Label>
                      <Input type="number" min={1} value={editForm.loyalty_points_per_purchase} onChange={(e) => setEditForm({ ...editForm, loyalty_points_per_purchase: Number(e.target.value) })} data-testid="biz-edit-loyalty-pts" />
                    </div>
                    <div>
                      <Label className="text-sm">Meta de puntos</Label>
                      <Input type="number" min={1} value={editForm.loyalty_threshold} onChange={(e) => setEditForm({ ...editForm, loyalty_threshold: Number(e.target.value) })} data-testid="biz-edit-loyalty-threshold" />
                    </div>
                    <div>
                      <Label className="text-sm">Etiqueta de compra</Label>
                      <Input value={editForm.loyalty_purchase_label || ""} onChange={(e) => setEditForm({ ...editForm, loyalty_purchase_label: e.target.value })} data-testid="biz-edit-loyalty-label" />
                    </div>
                    <div>
                      <Label className="text-sm">Premio</Label>
                      <Input value={editForm.loyalty_reward || ""} onChange={(e) => setEditForm({ ...editForm, loyalty_reward: e.target.value })} data-testid="biz-edit-loyalty-reward" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DrawerFooter>
            <div className="flex gap-2 justify-end">
              <DrawerClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DrawerClose>
              <Button className="btn-primary" onClick={saveEdit} disabled={editBusy} data-testid="biz-edit-save">
                {editBusy ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

const StatusBadge = ({ status, needsReview }) => {
  if (status === "cancelled") return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelado</Badge>;
  if (status === "approved" && needsReview) return <Badge className="bg-amber-500 text-white"><Clock className="w-3 h-3 mr-1" />Por revisar</Badge>;
  if (status === "approved") return <Badge className="bg-[#7C3AED] text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Verificado</Badge>;
  if (status === "rejected") return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rechazado</Badge>;
  return <Badge className="bg-zinc-200 text-zinc-900"><Clock className="w-3 h-3 mr-1" />Pendiente</Badge>;
};

function DirectoriesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/directories");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (it) => { setEditing(it); setName(it.name || ""); };

  const save = async () => {
    if (!name.trim()) return toast.error("Captura un nombre para el directorio");
    setBusy(true);
    try {
      await api.post("/admin/directories", { postal_code: editing.postal_code, name: name.trim() });
      toast.success(`Directorio CP ${editing.postal_code} renombrado`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/directories/${editing.postal_code}`);
      toast.success("Nombre eliminado — se mostrará solo el CP");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Nombres de directorios</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Asigna un nombre amigable a cada código postal (ej. "OFERTAS ZONA CENTRO"). El nombre aparecerá como título cuando los vecinos visiten su directorio.
          </p>
        </div>
        <div className="text-xs text-muted-foreground">{items.length} CPs detectados</div>
      </div>

      {loading ? <div className="text-muted-foreground mt-4">Cargando…</div> : items.length === 0 ? (
        <div className="text-sm text-muted-foreground mt-4">Aún no hay códigos postales con comercios.</div>
      ) : (
        <div className="overflow-x-auto mt-5">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">CP</th><th>Nombre del directorio</th><th>Colonia / Ciudad</th><th>Comercios</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.postal_code} className="border-b border-border" data-testid={`dir-row-${it.postal_code}`}>
                  <td className="py-3 font-mono font-medium">{it.postal_code}</td>
                  <td>
                    {it.name ? (
                      <span className="font-medium text-[#5B21B6]">{it.name}</span>
                    ) : (
                      <span className="text-zinc-400 italic">— sin nombre —</span>
                    )}
                  </td>
                  <td className="text-xs text-muted-foreground">{it.colonia} {it.city ? `· ${it.city}` : ""}</td>
                  <td>
                    <Badge variant="outline">{it.active_count}/{it.count}</Badge>
                  </td>
                  <td>
                    <Button variant="outline" size="sm" onClick={() => openEdit(it)} data-testid={`dir-edit-${it.postal_code}`}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" />
                      {it.name ? "Editar" : "Asignar nombre"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="dir-edit-dialog">
          <DialogHeader>
            <DialogTitle>Nombre del directorio CP {editing?.postal_code}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. OFERTAS ZONA CENTRO"
              className="text-lg"
              data-testid="dir-name-input"
            />
            <div className="text-xs text-muted-foreground">
              Este nombre aparecerá como título cuando los vecinos del CP {editing?.postal_code} entren a su directorio: <strong>"Bienvenido a {name || "..."}"</strong>
            </div>
              <div className="flex items-center justify-between gap-2 pt-2">
              {editing?.name ? (
                <Button variant="outline" onClick={clear} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="dir-clear-btn">
                  Quitar nombre
                </Button>
              ) : <div />}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
                <Button onClick={save} disabled={busy} className="btn-primary" data-testid="dir-save-btn">
                  {busy ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomersTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);
  const { impersonate } = useAuth();

  const enterAs = async (type, id) => {
    try {
      await impersonate(type, id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo entrar a la cuenta");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/customers${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setEditing(c);
    setEditForm({
      name: c.name || "",
      whatsapp: c.whatsapp || "",
      email: c.email || "",
      password: "",
    });
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {};
      const orig = editing;
      for (const [k, v] of Object.entries(editForm)) {
        if (k === "password" && v) { payload.password = v; continue; }
        if (v !== (orig[k] || "")) payload[k] = v;
      }
      if (Object.keys(payload).length === 0) {
        toast.error("Sin cambios");
        setEditBusy(false);
        return;
      }
      await api.put(`/admin/customers/${editing.id}`, payload);
      toast.success("Cliente actualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setEditBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Clientes</h3>
          <p className="text-sm text-muted-foreground mt-1">Usuarios registrados como clientes con sus puntos y boletos de rifa.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, tel o correo" data-testid="admin-cust-search" />
          </div>
          <Button onClick={load} size="sm" data-testid="admin-cust-search-btn">Buscar</Button>
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Cargando…</div> : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="admin-cust-empty">
          No se encontraron clientes.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">Cliente</th><th>WhatsApp</th><th>Correo</th><th>Puntos</th><th>Boletos rifa</th><th>Estado</th><th>Registro</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-border" data-testid={`admin-cust-row-${c.id}`}>
                  <td className="py-3 font-medium">{c.name || "—"}</td>
                  <td className="font-mono text-xs">{c.whatsapp || "—"}</td>
                  <td className="text-xs">{c.email || "—"}</td>
                  <td><Badge variant="outline" className="text-[#7C3AED] border-[#C4B5FD]">{c.loyalty_points}</Badge></td>
                  <td><Badge variant="outline">{c.raffle_tickets}</Badge></td>
                  <td>{c.is_active ? <Badge className="bg-[#7C3AED] text-white">Activo</Badge> : <Badge variant="destructive">Inactivo</Badge>}</td>
                  <td className="text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("es-MX") : "—"}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => enterAs("customer", c.id)} title="Entrar como este cliente" data-testid={`admin-cust-impersonate-${c.id}`}>
                        <LogIn className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`admin-cust-edit-${c.id}`}>
                        <Edit3 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Drawer open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DrawerContent className="max-h-[85vh]" data-testid="admin-cust-edit-drawer">
          <DrawerHeader>
            <DrawerTitle>Editar cliente</DrawerTitle>
          </DrawerHeader>
          {editing && (
            <div className="px-4 pb-6 space-y-4 overflow-y-auto">
              <div>
                <Label className="text-sm">Nombre</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="cust-edit-name" />
              </div>
              <div>
                <Label className="text-sm">WhatsApp</Label>
                <Input value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value.replace(/\D/g, "").slice(0, 12) })} inputMode="numeric" data-testid="cust-edit-whatsapp" />
              </div>
              <div>
                <Label className="text-sm">Correo electrónico</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="cust-edit-email" />
              </div>
              <div>
                <Label className="text-sm">Nueva contraseña <span className="text-muted-foreground font-normal">(dejar vacío para no cambiar)</span></Label>
                <Input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nueva contraseña" data-testid="cust-edit-password" />
              </div>
            </div>
          )}
          <DrawerFooter>
            <div className="flex gap-2 justify-end">
              <DrawerClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DrawerClose>
              <Button className="btn-primary" onClick={saveEdit} disabled={editBusy} data-testid="cust-edit-save">
                {editBusy ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
